import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldHint,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@repo/ui";
import { sanitizeNDCInput, formatNDC } from "@repo/utils";
import { Loader2, Package } from "lucide-react";
import { useNDCInputHandlers } from "@/hooks/use-ndc-input-handlers";
import type { NDCLookupResult } from "@/hooks/use-ndc-lookup";
import type { InventoryItem } from "./inventory-search-combobox";
import { createInventoryItem } from "@/lib/api/inventory";
import { ApiClientError } from "@/lib/api/client";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { useApproval } from "@/context/approval-context";

const productSchema = z.object({
  ndc: z
    .string()
    .min(1, "NDC is required")
    .regex(/^\d{5}-\d{4}-\d{2}$/, "Invalid NDC format (e.g., 12345-6789-01)"),
  itemName: z.string().min(1, "Item name is required"),
  initialStock: z.coerce.number().min(0, "Initial stock must be 0 or greater"),
  reorderThreshold: z.coerce
    .number()
    .min(0, "Reorder threshold must be 0 or greater"),
});

type ProductFormData = z.infer<typeof productSchema>;

export interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductCreated?: (item: InventoryItem) => void;
  /** Pre-fill the search query from the combobox */
  initialSearchQuery?: string;
}

export function CreateProductDialog({
  open,
  onOpenChange,
  onProductCreated,
  initialSearchQuery = "",
}: CreateProductDialogProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [drugType, setDrugType] = React.useState<string>("schedule_ii");
  const [noMatch, setNoMatch] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      ndc: "",
      itemName: "",
      initialStock: 0,
      reorderThreshold: 0,
    },
  });

  const ndcValue = watch("ndc");

  const onNDCResult = React.useCallback(
    (data: NDCLookupResult) => {
      if (data.resolvedNDC) {
        setValue("ndc", data.resolvedNDC);
      }
      setValue("itemName", data.item_name);
      if (data.drug_type) {
        setDrugType(data.drug_type);
      }
      setNoMatch(false);
    },
    [setValue]
  );

  const onNDCNotFound = React.useCallback(
    (digits: string) => {
      setNoMatch(true);
      if (digits.length === 11) {
        setValue("ndc", formatNDC(digits));
      } else {
        setError("ndc", {
          message:
            "Could not resolve 10-digit NDC. Enter the full 11-digit NDC or try a different code.",
        });
      }
    },
    [setValue, setError]
  );

  const {
    handleKeyDown: ndcKeyDown,
    handleBlur: ndcBlur,
    performLookup: performNDCLookup,
    isLoading: isLookingUpNDC,
  } = useNDCInputHandlers({
    onResult: onNDCResult,
    onNotFound: onNDCNotFound,
  });

  // Pre-fill from initial search query — detect if it looks like an NDC (digits/dashes)
  React.useEffect(() => {
    if (open) {
      setDrugType("schedule_ii");
      setNoMatch(false);

      const query = initialSearchQuery || "";
      const digitsOnly = query.replace(/\D/g, "");
      const looksLikeNDC =
        digitsOnly.length >= 10 && /^[\d-]+$/.test(query.trim());

      reset({
        ndc: looksLikeNDC ? query.trim() : "",
        itemName: looksLikeNDC ? "" : query,
        initialStock: 0,
        reorderThreshold: 0,
      });

      // If it looks like an NDC, trigger a lookup to auto-fill item name
      if (looksLikeNDC) {
        performNDCLookup(digitsOnly);
      }
    }
  }, [open, initialSearchQuery, reset, performNDCLookup]);

  const submitProduct = async (
    data: ProductFormData,
    approvalToken?: string
  ) => {
    setIsSubmitting(true);

    try {
      const resolvedDrugType = drugType === "non_controlled" ? null : drugType;
      const result = await createInventoryItem({
        ndc: data.ndc,
        item_name: data.itemName,
        drug_type: resolvedDrugType,
        initial_stock: data.initialStock,
        reorder_threshold: data.reorderThreshold,
        ...(approvalToken ? { approvalToken } : {}),
      });

      // Build InventoryItem from response to pass back
      const newItem: InventoryItem = {
        id: result.data.id,
        ndc: result.data.ndc,
        itemName: result.data.item_name,
        drugType: result.data.drug_type || "schedule_ii",
        label: `${result.data.ndc} — ${result.data.item_name}`,
      };

      toast({
        title: "Product Added",
        description: `${data.itemName} has been added to inventory.`,
        variant: "success",
      });

      onProductCreated?.(newItem);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (
          handlePermissionError(error, {
            requestApproval,
            toast,
            retryFn: async (token) => submitProduct(data, token),
            actionDescription: `Add product ${data.itemName}`,
          })
        )
          return;
      }
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data: ProductFormData) => submitProduct(data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Add New Product
          </DialogTitle>
          <DialogDescription>
            Add a new controlled substance to your inventory. Enter the NDC to
            auto-fill drug details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* NDC */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="create-ndc">NDC Code *</Label>
              <FieldHint>
                The 10 or 11 digit National Drug Code. Type it and C2 Vault will
                auto fill the drug name and details from the federal database.
              </FieldHint>
            </div>
            <div className="relative">
              <Input
                id="create-ndc"
                placeholder="10 or 11 digit NDC"
                className="font-mono"
                {...register("ndc")}
                onChange={(e) => {
                  const rawDigits = sanitizeNDCInput(e.target.value);
                  setValue("ndc", rawDigits);
                  clearErrors("ndc");
                  setNoMatch(false);
                }}
                onKeyDown={ndcKeyDown}
                onBlur={ndcBlur}
              />
              {isLookingUpNDC && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            {errors.ndc && (
              <p className="text-sm text-destructive">{errors.ndc.message}</p>
            )}
            {ndcValue && !errors.ndc && (
              <p className="text-xs text-muted-foreground">
                Format: 10 or 11 digits without dashes
              </p>
            )}
          </div>

          {/* Item Name */}
          <div className="space-y-2">
            <Label htmlFor="create-itemName">Item Name *</Label>
            <Input
              id="create-itemName"
              placeholder="Enter medication name"
              {...register("itemName")}
            />
            {errors.itemName && (
              <p className="text-sm text-destructive">
                {errors.itemName.message}
              </p>
            )}
          </div>

          {/* Schedule — visible when NDC lookup has no match */}
          {noMatch && (
            <div className="space-y-2">
              <Label htmlFor="create-schedule">Schedule</Label>
              <Select value={drugType} onValueChange={setDrugType}>
                <SelectTrigger id="create-schedule">
                  <SelectValue placeholder="Select schedule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="non_controlled">Non-Controlled</SelectItem>
                  <SelectItem value="schedule_ii">Schedule II</SelectItem>
                  <SelectItem value="schedule_iii">Schedule III</SelectItem>
                  <SelectItem value="schedule_iv">Schedule IV</SelectItem>
                  <SelectItem value="schedule_v">Schedule V</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                No match found — select the drug schedule manually
              </p>
            </div>
          )}

          {/* Initial Stock & Reorder Threshold — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="create-initialStock">Initial Stock</Label>
                <FieldHint>
                  How much you have on hand right now. This is the starting
                  point for the audit trail. Receipts and dispenses adjust from
                  here.
                </FieldHint>
              </div>
              <Input
                id="create-initialStock"
                type="number"
                placeholder="0"
                {...register("initialStock")}
              />
              {errors.initialStock && (
                <p className="text-sm text-destructive">
                  {errors.initialStock.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="create-reorderThreshold">
                  Minimum Stock Level
                </Label>
                <FieldHint>
                  The lowest amount you want to keep in stock. Once stock drops
                  to or below this level, the item shows a low stock alert.
                </FieldHint>
              </div>
              <Input
                id="create-reorderThreshold"
                type="number"
                placeholder="10"
                {...register("reorderThreshold")}
              />
              {errors.reorderThreshold && (
                <p className="text-sm text-destructive">
                  {errors.reorderThreshold.message}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding…
                </>
              ) : (
                "Add Product"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
